import React, { Component } from "react";
import NextGenPlayer from "../Components/NextGenPlayer";
import { createAnalyticsStubPlugin } from "../Plugins/builtins/analyticsStub";
import { createSubtitlesPlugin } from "../Plugins/builtins/subtitlesPlugin";
import { createAudioTracksPlugin } from "../Plugins/builtins/audioTracksPlugin";
import { createResumePlaybackPlugin } from "../Plugins/builtins/resumePlaybackPlugin";
import { createChaptersPlugin } from "../Plugins/builtins/chaptersPlugin";
import { createLocalProgressStorage } from "../Services/ProgressStorage";
import { predictProviderName } from "../Streaming/StreamingProviderRegistry";
import { PlayerEvent } from "../Core/PlayerEvents";
import { CastingEvent } from "../Providers/CastingProvider";
import {
  LAB_MP4,
  LAB_HLS,
  LAB_SUBTITLES,
  LAB_AUDIO_TRACKS,
  LAB_CHAPTERS,
} from "./fixtures/samples";
import CompanionPresentationSurface from "../tpg/CompanionPresentationSurface";
import { MediaLoopMode } from "../tpg/MediaAsset";
import {
  createModernAvodPlugin,
  resolveModernVideoAdsFlags,
} from "../Advertising";

/** Public IMA sample VAST (lab harness only — never used by production players). */
var LAB_VAST_TAG =
  "https://pubads.g.doubleclick.net/gampad/ads?iu=/21775744923/external/single_ad_samples&sz=640x480&cust_params=sample_ct%3Dlinear&ciu_szs=300x250%2C728x90&gdfp_req=1&output=vast&unviewed_position_start=1&env=vp&impl=s&correlator=";

function labModernAdsQueryEnabled() {
  try {
    if (typeof window === "undefined" || !window.location) return false;
    var q = window.location.search || "";
    return /[?&]modernAds=1(?:&|$)/.test(q);
  } catch (e) {
    return false;
  }
}

/** Lab-only companion artwork + short silent-friendly demo audio (public domain-ish sample). */
var LAB_COMPANION = {
  image: {
    url: "https://upload.wikimedia.org/wikipedia/commons/thumb/4/47/PNG_transparency_demonstration_1.png/280px-PNG_transparency_demonstration_1.png",
    role: "companion_image",
  },
  audio: {
    url: "https://upload.wikimedia.org/wikipedia/commons/transcoded/8/87/Ascending_thirds.ogg/Ascending_thirds.ogg.mp3",
    role: "companion_audio",
  },
  loopMode: MediaLoopMode.NONE,
  fit: "contain",
  background: "#1a1520",
};

/**
 * Experimental lab — /player-lab (Phase 4 casting + Phase B1 companion prototype).
 */
class PlayerLabPage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      sourceUrl: LAB_MP4,
      appliedUrl: LAB_MP4,
      contentId: "lab-big-buck-bunny",
      events: [],
      readyInfo: null,
      quality: null,
      diagnostics: null,
      castDiag: null,
      pluginsEnabled: {
        subtitles: true,
        audio: true,
        resume: true,
        chapters: true,
        analytics: true,
      },
      a11y: {
        highContrast: false,
        controlScale: 1,
      },
      castingLab: {
        casting: true,
        castingSimulate: true,
        chromecast: true,
        airplay: true,
        tvNavigation: false,
      },
      labMode: "player",
      companionLoop: MediaLoopMode.NONE,
      /** Gated: only when /player-lab?modernAds=1 — does not affect production players. */
      modernAdsLab: {
        queryEnabled: labModernAdsQueryEnabled(),
        pluginOn: false,
        vastTagUrl: LAB_VAST_TAG,
      },
    };
    this.handleSourceChange = this.handleSourceChange.bind(this);
    this.handleApply = this.handleApply.bind(this);
    this.handleSample = this.handleSample.bind(this);
    this.pushEvent = this.pushEvent.bind(this);
    this.handlePlaybackEvent = this.handlePlaybackEvent.bind(this);
    this.togglePlugin = this.togglePlugin.bind(this);
    this.toggleCastingFlag = this.toggleCastingFlag.bind(this);
    this._diagTimer = null;
    this._readyApi = null;
  }

  componentWillUnmount() {
    if (this._diagTimer) {
      clearInterval(this._diagTimer);
    }
  }

  pushEvent(entry) {
    this.setState(function (prev) {
      return {
        events: [entry].concat(prev.events).slice(0, 50),
      };
    });
  }

  handleSourceChange(e) {
    this.setState({ sourceUrl: e.target.value });
  }

  handleSample(url, contentId) {
    this.setState({
      sourceUrl: url,
      appliedUrl: url,
      contentId: contentId || "lab-custom",
      events: [],
      quality: null,
    });
  }

  handleApply(e) {
    e.preventDefault();
    this.setState({
      appliedUrl: this.state.sourceUrl,
      events: [],
      quality: null,
      diagnostics: null,
      castDiag: null,
    });
  }

  togglePlugin(key) {
    this.setState(function (prev) {
      var next = Object.assign({}, prev.pluginsEnabled);
      next[key] = !next[key];
      return { pluginsEnabled: next };
    });
  }

  toggleCastingFlag(key) {
    this.setState(function (prev) {
      var next = Object.assign({}, prev.castingLab);
      next[key] = !next[key];
      return { castingLab: next };
    });
  }

  handlePlaybackEvent(name, payload) {
    this.pushEvent({
      at: new Date().toISOString(),
      type: name,
      detail:
        payload && payload.message
          ? payload.message
          : payload && payload.chapter
            ? payload.chapter.title
            : payload && payload.position != null
              ? String(Math.floor(payload.position)) + "s"
              : payload && payload.id
                ? String(payload.id)
                : payload && payload.provider
                  ? String(payload.provider)
                  : payload && payload.target
                    ? String(payload.target)
                    : "",
    });
  }

  buildPlugins() {
    var enabled = this.state.pluginsEnabled;
    var list = [];
    if (enabled.analytics) {
      var self = this;
      list.push(
        createAnalyticsStubPlugin({
          sink: function (evt) {
            self.pushEvent({
              at: new Date().toISOString(),
              type: "plugin:" + evt.type,
            });
          },
        })
      );
    }
    if (enabled.subtitles) {
      list.push(createSubtitlesPlugin());
    }
    if (enabled.audio) {
      list.push(createAudioTracksPlugin());
    }
    if (enabled.resume) {
      list.push(
        createResumePlaybackPlugin({
          contentId: this.state.contentId,
          storage: createLocalProgressStorage(),
          saveIntervalMs: 4000,
          autoResume: true,
        })
      );
    }
    if (enabled.chapters) {
      list.push(createChaptersPlugin());
    }
    var modernLab = this.state.modernAdsLab;
    if (modernLab && modernLab.queryEnabled && modernLab.pluginOn) {
      list.push(
        createModernAvodPlugin({
          forceLab: true,
          isLive: false,
          schedule: {
            vastTagUrl: modernLab.vastTagUrl || LAB_VAST_TAG,
          },
          features: { modernImaAds: true },
          avod: {
            modern: {
              enabled: false,
              live_ads_enabled: false,
            },
          },
        })
      );
    }
    return list;
  }

  render() {
    var url = this.state.appliedUrl || this.state.sourceUrl;
    var self = this;
    var predicted = predictProviderName(url);
    var pe = this.state.pluginsEnabled;
    var cl = this.state.castingLab;

    var config = {
      source: url,
      contentId: this.state.contentId,
      poster: "",
      title: "Player Lab Sample",
      subtitles: pe.subtitles ? LAB_SUBTITLES : [],
      audioTracks: pe.audio ? LAB_AUDIO_TRACKS : [],
      chapters: pe.chapters ? LAB_CHAPTERS : [],
      plugins: this.buildPlugins(),
      branding: {
        label: "Player Lab",
        accent: "#3d8bfd",
      },
      casting: {
        simulate: !!cl.castingSimulate,
        chromecast: !!cl.chromecast,
        airplay: !!cl.airplay,
        brand: { castButtonLabel: "Cast" },
        simulateOptions: { deviceName: "Lab Simulated TV" },
      },
      theme: {
        colors: {
          accent: "#3d8bfd",
        },
        subtitles: {
          color: "#fff8e7",
          background: "rgba(0,0,0,0.7)",
          fontSize: "1.1em",
        },
      },
      features: {
        fullscreen: true,
        pictureInPicture: true,
        playbackSpeed: true,
        keyboardShortcuts: true,
        qualitySelection: true,
        subtitles: pe.subtitles,
        audioTracks: pe.audio,
        chapters: pe.chapters,
        continueWatching: pe.resume,
        skipButtons: true,
        skipInterval: 10,
        seekZones: true,
        autoHideControls: true,
        highContrast: this.state.a11y.highContrast,
        controlScale: this.state.a11y.controlScale,
        casting: !!cl.casting,
        castingSimulate: !!cl.castingSimulate,
        chromecast: !!cl.chromecast,
        airplay: !!cl.airplay,
        tvNavigation: !!cl.tvNavigation,
        analytics: pe.analytics,
        watermarks: false,
      },
      autoplay: false,
      volume: 0.8,
    };

    var playerKey =
      url +
      "|" +
      this.state.contentId +
      "|" +
      JSON.stringify(pe) +
      "|" +
      JSON.stringify(cl) +
      "|" +
      this.state.a11y.highContrast +
      "|" +
      this.state.a11y.controlScale +
      "|modernAds:" +
      (this.state.modernAdsLab &&
      this.state.modernAdsLab.queryEnabled &&
      this.state.modernAdsLab.pluginOn
        ? "1"
        : "0");

    var castDiag = this.state.castDiag;

    return (
      <div style={styles.page}>
        <header style={styles.header}>
          <h1 style={styles.h1}>TPG Player Lab</h1>
          <p style={styles.sub}>
            Casting diagnostics plus Phase B1 Companion Image + Audio prototype.
            Production HostPlaybackPage is unchanged for companion (lab only).
            Modern IMA harness: open{" "}
            <code>/player-lab?modernAds=1</code> (scaffolding; live ads stay off).
          </p>
          <div style={styles.samples}>
            <button
              type="button"
              style={styles.sampleBtn}
              onClick={function () {
                self.setState({ labMode: "player" });
              }}
            >
              Video player
            </button>
            <button
              type="button"
              style={styles.sampleBtn}
              onClick={function () {
                self.setState({ labMode: "companion" });
              }}
            >
              Companion presentation
            </button>
          </div>
        </header>

        {this.state.labMode === "companion" ? (
          <section style={{ marginBottom: 24 }}>
            <p style={styles.sub}>
              Companion prototype — image + optional audio, independent loop modes.
            </p>
            <div style={styles.samples}>
              <button
                type="button"
                style={styles.sampleBtn}
                onClick={function () {
                  self.setState({ companionLoop: MediaLoopMode.NONE });
                }}
              >
                Loop: none
              </button>
              <button
                type="button"
                style={styles.sampleBtn}
                onClick={function () {
                  self.setState({ companionLoop: MediaLoopMode.AUDIO_ONLY });
                }}
              >
                Loop: audio
              </button>
              <button
                type="button"
                style={styles.sampleBtn}
                onClick={function () {
                  self.setState({ companionLoop: MediaLoopMode.PRESENTATION });
                }}
              >
                Loop: presentation
              </button>
            </div>
            <CompanionPresentationSurface
              title="Lab companion"
              companion={Object.assign({}, LAB_COMPANION, {
                loopMode: this.state.companionLoop,
              })}
              onEvent={function (name, payload) {
                self.handlePlaybackEvent(name, payload || {});
              }}
            />
          </section>
        ) : null}

        {this.state.labMode === "player" ? (
        <React.Fragment>
        <div style={styles.samples}>
          <button
            type="button"
            style={styles.sampleBtn}
            onClick={function () {
              self.handleSample(LAB_MP4, "lab-big-buck-bunny");
            }}
          >
            MP4 + demos
          </button>
          <button
            type="button"
            style={styles.sampleBtn}
            onClick={function () {
              self.handleSample(LAB_HLS, "lab-bipbop-hls");
            }}
          >
            HLS adaptive
          </button>
        </div>

        {this.state.modernAdsLab && this.state.modernAdsLab.queryEnabled ? (
          <section style={styles.panel}>
            <h2 style={styles.h2}>Modern IMA harness (lab only)</h2>
            <p style={styles.sub}>
              Scaffolding loads IMA only when toggled. AdsManager playback wiring is
              deferred. Live ads stay off. Flags:{" "}
              <code>
                {JSON.stringify(
                  resolveModernVideoAdsFlags({
                    forceLab: !!this.state.modernAdsLab.pluginOn,
                  })
                )}
              </code>
            </p>
            <label style={styles.check}>
              <input
                type="checkbox"
                checked={!!this.state.modernAdsLab.pluginOn}
                onChange={function () {
                  self.setState(function (prev) {
                    var next = Object.assign({}, prev.modernAdsLab);
                    next.pluginOn = !next.pluginOn;
                    return { modernAdsLab: next };
                  });
                }}
              />{" "}
              Attach createModernAvodPlugin (forceLab)
            </label>
            <label style={styles.label}>
              VAST tag URL
              <input
                style={styles.input}
                type="url"
                value={this.state.modernAdsLab.vastTagUrl}
                onChange={function (e) {
                  var value = e.target.value;
                  self.setState(function (prev) {
                    var next = Object.assign({}, prev.modernAdsLab);
                    next.vastTagUrl = value;
                    return { modernAdsLab: next };
                  });
                }}
              />
            </label>
          </section>
        ) : null}

        <form style={styles.form} onSubmit={this.handleApply}>
          <label style={styles.label}>
            Media URL
            <input
              style={styles.input}
              type="url"
              value={this.state.sourceUrl}
              onChange={this.handleSourceChange}
            />
          </label>
          <button type="submit" style={styles.button}>
            Load
          </button>
        </form>

        <section style={styles.toggles}>
          <h2 style={styles.h2}>Plugins</h2>
          {["subtitles", "audio", "resume", "chapters", "analytics"].map(
            function (key) {
              return (
                <label key={key} style={styles.check}>
                  <input
                    type="checkbox"
                    checked={!!pe[key]}
                    onChange={function () {
                      self.togglePlugin(key);
                    }}
                  />{" "}
                  {key}
                </label>
              );
            }
          )}
          <h2 style={styles.h2}>Casting & TV</h2>
          {[
            ["casting", "Enable casting"],
            ["castingSimulate", "Simulated cast (lab)"],
            ["chromecast", "Chromecast provider"],
            ["airplay", "AirPlay provider"],
            ["tvNavigation", "TV spatial navigation"],
          ].map(function (pair) {
            return (
              <label key={pair[0]} style={styles.check}>
                <input
                  type="checkbox"
                  checked={!!cl[pair[0]]}
                  onChange={function () {
                    self.toggleCastingFlag(pair[0]);
                  }}
                />{" "}
                {pair[1]}
              </label>
            );
          })}
          <h2 style={styles.h2}>Accessibility</h2>
          <label style={styles.check}>
            <input
              type="checkbox"
              checked={this.state.a11y.highContrast}
              onChange={function () {
                self.setState({
                  a11y: Object.assign({}, self.state.a11y, {
                    highContrast: !self.state.a11y.highContrast,
                  }),
                });
              }}
            />{" "}
            High contrast
          </label>
          <label style={styles.check}>
            Control scale{" "}
            <select
              value={String(this.state.a11y.controlScale)}
              onChange={function (e) {
                self.setState({
                  a11y: Object.assign({}, self.state.a11y, {
                    controlScale: Number(e.target.value),
                  }),
                });
              }}
            >
              <option value="1">1×</option>
              <option value="1.25">1.25×</option>
              <option value="1.5">1.5×</option>
            </select>
          </label>
        </section>

        <NextGenPlayer
          key={playerKey}
          config={config}
          onReady={function (info) {
            self._readyApi = info.api;
            self.setState({ readyInfo: info });
            self.pushEvent({
              at: new Date().toISOString(),
              type: PlayerEvent.PLAYER_READY,
              detail: info.provider || "",
            });
            if (self._diagTimer) {
              clearInterval(self._diagTimer);
            }
            self._diagTimer = setInterval(function () {
              var next = {};
              if (info.api && info.api.getBufferStats) {
                next.diagnostics = {
                  buffer: info.api.getBufferStats(),
                  quality: info.api.getQuality && info.api.getQuality(),
                  text: info.api.getTextTracks && info.api.getTextTracks(),
                  audio: info.api.getAudioTracks && info.api.getAudioTracks(),
                };
              }
              if (info.api && info.api.getCastDiagnostics) {
                next.castDiag = info.api.getCastDiagnostics();
              }
              self.setState(next);
            }, 1500);
          }}
          onError={function (err) {
            self.pushEvent({
              at: new Date().toISOString(),
              type: PlayerEvent.PLAYBACK_ERROR,
              detail: err && err.message,
            });
          }}
          onEnded={function () {
            self.pushEvent({
              at: new Date().toISOString(),
              type: PlayerEvent.PLAYBACK_ENDED,
            });
          }}
          onQualityChange={function (q) {
            self.setState({ quality: q });
          }}
          onPlaybackEvent={this.handlePlaybackEvent}
        />

        <section style={styles.castSection}>
          <h2 style={styles.h2}>Casting Diagnostics</h2>
          <p style={styles.hint}>
            Development-only. Use the Cast control (or API) with Simulated cast
            enabled when no hardware is available. Drop session via console:{" "}
            <code>
              readyInfo.casting.getActiveProvider().simulateDrop()
            </code>
          </p>
          <div style={styles.castActions}>
            <button
              type="button"
              style={styles.sampleBtn}
              onClick={function () {
                if (self._readyApi && self._readyApi.startCast) {
                  self._readyApi.startCast("simulated").catch(function (e) {
                    self.pushEvent({
                      at: new Date().toISOString(),
                      type: CastingEvent.ERROR,
                      detail: e && e.message,
                    });
                  });
                }
              }}
            >
              Start simulated cast
            </button>
            <button
              type="button"
              style={styles.sampleBtn}
              onClick={function () {
                if (self._readyApi && self._readyApi.stopCast) {
                  self._readyApi.stopCast();
                }
              }}
            >
              Stop cast
            </button>
            <button
              type="button"
              style={styles.sampleBtn}
              onClick={function () {
                var c =
                  self.state.readyInfo && self.state.readyInfo.casting;
                var p = c && c.getActiveProvider && c.getActiveProvider();
                if (p && p.simulateDrop) p.simulateDrop();
              }}
            >
              Simulate unexpected drop
            </button>
          </div>
          {castDiag ? (
            <div style={styles.grid}>
              <div style={styles.card}>
                <h3 style={styles.h3}>Target</h3>
                <p style={styles.hint}>
                  Playback: <code>{castDiag.target}</code>
                  <br />
                  Active: <code>{castDiag.activeProvider || "—"}</code>
                  <br />
                  Connection: <code>{castDiag.connection}</code>
                </p>
              </div>
              <div style={styles.card}>
                <h3 style={styles.h3}>Providers</h3>
                <ul style={styles.list}>
                  {(castDiag.providers || []).map(function (p, i) {
                    return (
                      <li key={i}>
                        <code>{p.name}</code> — available={" "}
                        {String(p.available)} · {p.connection}
                        <br />
                        <span style={{ opacity: 0.7 }}>
                          caps:{" "}
                          {Object.keys(p.capabilities || {})
                            .filter(function (k) {
                              return p.capabilities[k] === true;
                            })
                            .join(", ") || "—"}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
              <div style={styles.card}>
                <h3 style={styles.h3}>Devices / sync</h3>
                <p style={styles.hint}>
                  Devices:{" "}
                  {(castDiag.devices || [])
                    .map(function (d) {
                      return d.name;
                    })
                    .join(", ") || "none"}
                </p>
                {castDiag.sync ? (
                  <ul style={styles.list}>
                    <li>
                      Local t=
                      {Math.floor(
                        (castDiag.sync.local &&
                          castDiag.sync.local.currentTime) ||
                          0
                      )}
                      s
                    </li>
                    <li>
                      Remote t=
                      {Math.floor(
                        (castDiag.sync.lastRemoteState &&
                          castDiag.sync.lastRemoteState.currentTime) ||
                          0
                      )}
                      s · playing=
                      {String(
                        !!(
                          castDiag.sync.lastRemoteState &&
                          castDiag.sync.lastRemoteState.playing
                        )
                      )}
                    </li>
                  </ul>
                ) : null}
              </div>
              <div style={styles.card}>
                <h3 style={styles.h3}>Cast event log</h3>
                <ul style={styles.list}>
                  {(castDiag.eventLog || []).slice(0, 12).map(function (e, i) {
                    return (
                      <li key={i}>
                        <code>{e.at}</code> {e.type}
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          ) : (
            <p style={styles.hint}>Waiting for player ready…</p>
          )}
        </section>

        <section style={styles.grid}>
          <div style={styles.card}>
            <h2 style={styles.h2}>Provider</h2>
            <p style={styles.hint}>
              Predicted: <code>{predicted}</code>
            </p>
            {this.state.readyInfo ? (
              <p style={styles.hint}>
                Active: <code>{this.state.readyInfo.provider || "—"}</code>
                <br />
                Plugins:{" "}
                {this.state.readyInfo.plugins
                  .map(function (p) {
                    return p.name;
                  })
                  .join(", ")}
              </p>
            ) : null}
          </div>
          <div style={styles.card}>
            <h2 style={styles.h2}>Tracks</h2>
            {this.state.diagnostics && this.state.diagnostics.text ? (
              <ul style={styles.list}>
                <li>
                  Text:{" "}
                  {this.state.diagnostics.text
                    .map(function (t) {
                      return t.label + (t.active ? "*" : "");
                    })
                    .join(", ") || "—"}
                </li>
                <li>
                  Audio:{" "}
                  {(this.state.diagnostics.audio || [])
                    .map(function (t) {
                      return t.label + (t.active ? "*" : "");
                    })
                    .join(", ") || "—"}
                </li>
              </ul>
            ) : (
              <p style={styles.hint}>Waiting…</p>
            )}
          </div>
          <div style={styles.card}>
            <h2 style={styles.h2}>Diagnostics</h2>
            {this.state.diagnostics && this.state.diagnostics.buffer ? (
              <ul style={styles.list}>
                <li>
                  Startup:{" "}
                  <code>
                    {this.state.diagnostics.buffer.startupDelayMs != null
                      ? this.state.diagnostics.buffer.startupDelayMs + " ms"
                      : "—"}
                  </code>
                </li>
                <li>
                  Underruns:{" "}
                  <code>{this.state.diagnostics.buffer.underrunCount}</code>
                </li>
              </ul>
            ) : (
              <p style={styles.hint}>Waiting…</p>
            )}
          </div>
          <div style={styles.card}>
            <h2 style={styles.h2}>Shortcuts</h2>
            <p style={styles.hint}>
              Space/K play · J/L or ←/→ seek · ,/. frame · C captions · B/N
              chapters · M mute · F fullscreen · Cast button · TV mode: D-pad
              focus
            </p>
          </div>
        </section>
        </React.Fragment>
        ) : null}

        <section style={styles.meta}>
          <h2 style={styles.h2}>Event viewer</h2>
          <ul style={styles.list}>
            {this.state.events.map(function (evt, i) {
              return (
                <li key={i}>
                  <code>{evt.at}</code> — {evt.type}
                  {evt.detail ? ": " + evt.detail : ""}
                </li>
              );
            })}
          </ul>
        </section>
      </div>
    );
  }
}

var styles = {
  page: {
    minHeight: "100vh",
    background: "linear-gradient(160deg, #12141a 0%, #1a1f2b 50%, #0e1015 100%)",
    color: "#f2f4f8",
    padding: "32px 20px 64px",
    fontFamily:
      'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  header: { maxWidth: 1100, margin: "0 auto 24px" },
  h1: { margin: "0 0 8px", fontSize: "1.75rem", fontWeight: 650 },
  h2: { margin: "12px 0 8px", fontSize: "1.05rem" },
  h3: { margin: "0 0 8px", fontSize: "0.95rem" },
  sub: { margin: 0, opacity: 0.75, maxWidth: 820 },
  panel: {
    maxWidth: 1100,
    margin: "0 auto 16px",
    padding: "12px 14px",
    border: "1px solid rgba(255,255,255,0.14)",
    borderRadius: 8,
    background: "rgba(255,255,255,0.04)",
  },
  check: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    margin: "10px 0",
    fontSize: 13,
  },
  samples: {
    maxWidth: 1100,
    margin: "0 auto 12px",
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
  },
  sampleBtn: {
    padding: "8px 12px",
    borderRadius: 6,
    border: "1px solid rgba(255,255,255,0.18)",
    background: "rgba(255,255,255,0.06)",
    color: "inherit",
    cursor: "pointer",
    fontSize: 13,
  },
  form: {
    maxWidth: 1100,
    margin: "0 auto 12px",
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
    alignItems: "flex-end",
  },
  label: { flex: "1 1 320px", display: "flex", flexDirection: "column", gap: 6 },
  input: {
    padding: "10px 12px",
    borderRadius: 6,
    border: "1px solid rgba(255,255,255,0.15)",
    background: "rgba(0,0,0,0.35)",
    color: "inherit",
  },
  button: {
    padding: "10px 18px",
    borderRadius: 6,
    border: 0,
    background: "#3d8bfd",
    color: "#fff",
    fontWeight: 600,
    cursor: "pointer",
  },
  toggles: { maxWidth: 1100, margin: "0 auto 16px" },
  check: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    marginRight: 14,
    marginBottom: 8,
    fontSize: 14,
    opacity: 0.9,
  },
  castSection: { maxWidth: 1100, margin: "28px auto 0" },
  castActions: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    marginBottom: 12,
  },
  grid: {
    maxWidth: 1100,
    margin: "24px auto 0",
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: 16,
  },
  card: {
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 8,
    padding: 14,
  },
  meta: { maxWidth: 1100, margin: "28px auto 0" },
  hint: { opacity: 0.7, fontSize: 14, margin: "0 0 8px" },
  list: { paddingLeft: 18, fontSize: 13, opacity: 0.85, margin: 0 },
};

export default PlayerLabPage;
